import useTimeout from 'beautiful-react-hooks/useTimeout';
import { WEBCAM_STORAGE_KEY } from 'constants/common.const';
import { TIME_INITIAL_APP_DURATION } from 'constants/photo.const';
import { QUERY_STRING, SelfeRouteConst } from 'constants/route.const';
import { setSessionStorage } from 'helpers/common.helper';
import { isEqualVal } from 'helpers/string.helper';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import i18n from 'i18n';
import { find, forEach } from 'lodash';
import { CameraStateModel } from 'models/photo/camera.model';
import { FilterPhotoStateModel } from 'models/photo/filter.model';
import { FramePhotoStateModel } from 'models/photo/frame.model';
import { LanguageStateModel } from 'models/photo/language.model';
import {
  LayoutPhotoModel,
  LayoutPhotoStateModel,
} from 'models/photo/layout.model';
import { PhotoTakenStateModel } from 'models/photo/photo-taken.model';
import { ThemeCategoryPhotoStateModel } from 'models/photo/theme-category.model';
import {
  ThemeDetailPhotoModel,
  ThemePhotoStateModel,
} from 'models/photo/theme.model';
import qs from 'query-string';
import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetBoothApiActionQuery,
  useGetLanguagesApiActionQuery,
} from 'store/api/booth.slice-api';
import {
  useListCameraApiActionQuery,
  useListCapturePositionApiActionQuery,
  useStartCameraLiveViewApiActionMutation,
  useStopCameraLiveViewApiActionMutation,
} from 'store/api/camera.slice-api';
import { useGetFeedbackCriteriaApiActionQuery } from 'store/api/feedback.slice-api';
import { useListFilterApiActionQuery } from 'store/api/filter.slice-api';
import { useListFrameApiActionQuery } from 'store/api/frame.slice-api';
import {
  useListLayoutApiActionQuery,
  useListLayoutByThemeIdAndFrameIdApiActionQuery,
} from 'store/api/layout.slice-api';
import {
  useCloseLedLightApiActionMutation,
  useOpenLedLightApiActionMutation,
} from 'store/api/led-light.slice-api';
import {
  useCloseBillAcceptorApiActionMutation,
  useGetBillAcceptorApiActionQuery,
} from 'store/api/payment.slice-api';
import { useListPhotoTakenApiActionQuery } from 'store/api/photo-taken.slice-api';
import { useGetPlaylistApiActionQuery } from 'store/api/playlist-ads.slice-api';
import { useGetNumberOfRemainApiActionQuery } from 'store/api/print.slice-api';
import {
  useListThemeCategoryApiActionQuery,
  useListThemeCategoryByFrameIdApiActionQuery,
} from 'store/api/theme-category.slice.api';
import {
  useGetThemeDetailApiActionQuery,
  useListThemeByCategoryApiActionQuery,
  useListThemeByCategoryIdAndFrameIdApiActionQuery,
} from 'store/api/theme.slice-api';
import { setBoothStateAction } from 'store/features/app/boothSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { BackgroundCategoryPhotoStateModel } from '../models/photo/background-category.model';
import { BackgroundPhotoStateModel } from '../models/photo/background.model';
import { StickerCategoryPhotoStateModel } from '../models/photo/sticker-category.model';
import { StickerPhotoStateModel } from '../models/photo/sticker.model';
import { useListBackgroundCategoryApiActionQuery } from '../store/api/background-category.slice.api';
import { useListBackgroundByCategoryApiActionQuery } from '../store/api/background.slice-api';
import { useListStickerCategoryApiActionQuery } from '../store/api/sticker-category.slice.api';
import { useListStickerByCategoryApiActionQuery } from '../store/api/sticker.slice-api';

export const Context = React.createContext<BoothAppContextReturnTypes>({
  listLayoutPhotos: null,
  listFramePhotos: null,
  listPhotoTakens: null,
  listThemePhotos: null,
  listCapturePosition: null,
  listStickerPhotos: null,
  listBackgroundPhotos: null,
  listThemeCategoryPhotos: null,
  listBackgroundCategoryPhotos: null,
  listStickerCategoryPhotos: null,
  themeDetail: null,
  listFilterPhotos: null,
  listCameras: null,
  listLanguage: null,
  currentLayout: null,
});

interface BoothAppContextProps {}
type BoothAppContextReturnTypes = {
  listFramePhotos: FramePhotoStateModel['listFramePhotos'] | null;
  listLayoutPhotos: LayoutPhotoStateModel['listLayoutPhotos'] | null;
  listPhotoTakens: PhotoTakenStateModel['listPhotoTakens'] | null;
  listThemePhotos: ThemePhotoStateModel['listThemePhotos'] | null;
  listCapturePosition: CameraStateModel['listCapturePositions'] | null;
  listStickerPhotos: StickerPhotoStateModel['listStickerPhotos'] | null;
  listBackgroundPhotos:
    | BackgroundPhotoStateModel['listBackgroundPhotos']
    | null;
  listThemeCategoryPhotos:
    | ThemeCategoryPhotoStateModel['listThemeCategoryPhotos']
    | null;
  listStickerCategoryPhotos:
    | StickerCategoryPhotoStateModel['listStickerCategoryPhotos']
    | null;
  listBackgroundCategoryPhotos:
    | BackgroundCategoryPhotoStateModel['listBackgroundCategoryPhotos']
    | null;
  themeDetail: ThemeDetailPhotoModel | null;
  listFilterPhotos: FilterPhotoStateModel['listFilterPhotos'] | null;
  listCameras: CameraStateModel['listCameras'] | null;
  listLanguage: LanguageStateModel['listLanguage'] | null;
  currentLayout: LayoutPhotoModel | null;
};

export const BoothAppContext: FC<PropsWithChildren<BoothAppContextProps>> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getStartNodePath, currentRoute } = useNewPhotoLifeCycleStep();
  const {
    layoutId,
    themeCategoryId,
    stickerCategoryId,
    backgroundCategoryId,
    themeId,
    transactionId,
    frameId,
  } = useAppSelector((state) => state.selfeApp);
  const { isThemeBeforePayment, boothInfo, isEventBooth } = useAppSelector(
    (state) => state.booth,
  );
  const transactionStartedLiveView = useRef<Record<string, boolean> | null>({});

  useGetBillAcceptorApiActionQuery();
  useGetBoothApiActionQuery();

  const [startLiveViewApiAction] = useStartCameraLiveViewApiActionMutation();
  const [stopLiveViewApiAction] = useStopCameraLiveViewApiActionMutation();

  const [openLedLightApiAction] = useOpenLedLightApiActionMutation();
  const [closeLedLightApiAction] = useCloseLedLightApiActionMutation();

  const [closeBillAcceptor] = useCloseBillAcceptorApiActionMutation();
  const { data: listCameras } = useListCameraApiActionQuery();
  const { data: listCapturePosition } = useListCapturePositionApiActionQuery();
  const { refetch: refetchNumberOfRemain } =
    useGetNumberOfRemainApiActionQuery();

  // request list frame
  const { data: listFramePhotos, refetch: refetchListFramePhotos } =
    useListFrameApiActionQuery({
      pageIndex: 1,
      pageSize: 20,
    });
  // list layout
  const { data: listLayoutPhotosByFrame } = useListLayoutApiActionQuery({});

  const { data: listLayoutPhotosByThemeAndFrame } =
    useListLayoutByThemeIdAndFrameIdApiActionQuery({ themeId, frameId });

  const listLayoutPhotos = useMemo(
    () =>
      isThemeBeforePayment
        ? listLayoutPhotosByThemeAndFrame
        : listLayoutPhotosByFrame,
    [
      isThemeBeforePayment,
      listLayoutPhotosByFrame,
      listLayoutPhotosByThemeAndFrame,
    ],
  );

  // photo taken
  const { data: listPhotoTakens, error: errorListTaken } =
    useListPhotoTakenApiActionQuery(
      { transactionId },
      { skip: !transactionId },
    );

  // Theme category & theme
  const {
    data: listThemeCategoryByFramePhotos,
    refetch: refectListThemeCategoryByFramePhotos,
    isUninitialized: isThemeCategoryByFrameUninit,
  } = useListThemeCategoryByFrameIdApiActionQuery(
    { pageIndex: 1, pageSize: 9999, frameId },
    { skip: !isThemeBeforePayment || !frameId },
  );

  const { data: listThemeCategoryByLayoutPhotos } =
    useListThemeCategoryApiActionQuery(
      { pageIndex: 1, pageSize: 9999 },
      {
        skip: isEventBooth ? false : isThemeBeforePayment || !layoutId,
      },
    );

  // Use the appropriate data based on the condition
  const listThemeCategoryPhotos = useMemo(
    () =>
      isThemeBeforePayment && !isEventBooth
        ? listThemeCategoryByFramePhotos
        : listThemeCategoryByLayoutPhotos,
    [
      isEventBooth,
      isThemeBeforePayment,
      listThemeCategoryByFramePhotos,
      listThemeCategoryByLayoutPhotos,
    ],
  );

  // Sticker category & background
  const {
    data: listBackgroundCategoryPhotos,
    refetch: refetchListBackgroundCategoryPhotos,
  } = useListBackgroundCategoryApiActionQuery({
    pageIndex: 1,
    pageSize: 9999,
  });

  // Sticker category & sticker
  const {
    data: listStickerCategoryPhotos,
    refetch: refetchListStickerCategoryPhotos,
  } = useListStickerCategoryApiActionQuery({
    pageIndex: 1,
    pageSize: 9999,
  });

  const { data: listThemePhotosByLayoutId } =
    useListThemeByCategoryApiActionQuery(
      { themeCategoryId, layoutId, pageIndex: 1, pageSize: 9999 },
      {
        skip: isEventBooth ? false : !themeCategoryId || !layoutId,
      },
    );

  const { data: lisThemePhotosByFrameId } =
    useListThemeByCategoryIdAndFrameIdApiActionQuery(
      { themeCategoryId, frameId, pageIndex: 1, pageSize: 9999 },
      { skip: !themeCategoryId || !frameId },
    );

  const listThemePhotos = useMemo(
    () =>
      isThemeBeforePayment && !isEventBooth
        ? lisThemePhotosByFrameId
        : listThemePhotosByLayoutId,
    [
      isEventBooth,
      isThemeBeforePayment,
      lisThemePhotosByFrameId,
      listThemePhotosByLayoutId,
    ],
  );

  const { data: listBackgroundPhotos } =
    useListBackgroundByCategoryApiActionQuery(
      { backgroundCategoryId, pageIndex: 1, pageSize: 9999 },
      { skip: !backgroundCategoryId },
    );

  const { data: listStickerPhotos } = useListStickerByCategoryApiActionQuery(
    {
      stickerCategoryId,
      pageIndex: 1,
      pageSize: 9999,
    },
    { skip: !stickerCategoryId },
  );

  const { data: themeDetail } = useGetThemeDetailApiActionQuery(
    { layoutId, themeId },
    { skip: !layoutId || !themeId },
  );

  // Filter
  const {
    data: listFilterPhotos,
    refetch: refecthListFilterPhotos,
    isUninitialized: isFilterPhotoUninit,
  } = useListFilterApiActionQuery(null, {
    skip: !transactionId,
  });

  const { data: playlist } = useGetPlaylistApiActionQuery({
    pageIndex: 1,
    pageSize: 9999,
  });

  const { refetch: refetchFeedbackCriteria } =
    useGetFeedbackCriteriaApiActionQuery({
      pageIndex: 1,
      pageSize: 9999,
    });

  const currentLayout = useMemo(
    () =>
      find(listLayoutPhotos?.data, (o) => isEqualVal(o?.id, layoutId)) || null,
    [layoutId, listLayoutPhotos?.data],
  );

  const { data: listLanguages } = useGetLanguagesApiActionQuery({
    pageIndex: 0,
    pageSize: 9999,
  });

  useEffect(() => {
    refetchListFramePhotos();
    refetchListBackgroundCategoryPhotos();
    refetchListStickerCategoryPhotos();
    refetchFeedbackCriteria();
    if (!isFilterPhotoUninit) {
      refecthListFilterPhotos();
    }
    if (!isThemeCategoryByFrameUninit) {
      refectListThemeCategoryByFramePhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isThemeCategoryByFrameUninit,
    refetchListFramePhotos,
    refectListThemeCategoryByFramePhotos,
    refetchListBackgroundCategoryPhotos,
    refetchListStickerCategoryPhotos,
    refecthListFilterPhotos,
    refetchFeedbackCriteria,
    i18n.language,
  ]);

  const isHasErrorListTaken = useMemo(() => !!errorListTaken, [errorListTaken]);

  // redirect khi chưa chọn frameId
  useEffect(() => {
    if (
      !transactionId &&
      getStartNodePath() !== currentRoute?.route &&
      pathname !== SelfeRouteConst.GUIDE &&
      pathname !== SelfeRouteConst.GUIDE_API
    ) {
      navigate(getStartNodePath() || '');
    }
  }, [
    dispatch,
    transactionId,
    pathname,
    getStartNodePath,
    navigate,
    currentRoute,
    listCameras,
  ]);

  useEffect(() => {
    if (pathname === SelfeRouteConst.HOME) {
      refetchNumberOfRemain();
    }
  }, [pathname, refetchNumberOfRemain]);

  // close bill acceptor session
  useEffect(() => {
    if (!currentRoute?.isPageUsePayment) {
      closeBillAcceptor();
    }
  }, [closeBillAcceptor, currentRoute?.isPageUsePayment]);

  // Bật LiveView camera
  useEffect(() => {
    if (
      currentRoute?.isOpenCameraLiveView &&
      !transactionStartedLiveView?.current?.[transactionId]
    ) {
      transactionStartedLiveView.current = {
        ...(transactionStartedLiveView.current || {}),
        [transactionId]: true,
      };
      forEach(listCameras?.data, (camera) => {
        if (camera?.ID) startLiveViewApiAction({ id: camera?.ID });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startLiveViewApiAction,
    currentRoute?.isOpenCameraLiveView,
    transactionId,
  ]);

  // Tắt LiveView camera
  useEffect(() => {
    if (currentRoute?.isStopCameraLiveView) {
      transactionStartedLiveView.current = null;
      forEach(listCameras?.data, (camera) => {
        if (camera?.ID) stopLiveViewApiAction({ id: camera?.ID });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopLiveViewApiAction, currentRoute?.isStopCameraLiveView]);

  // Bật LED Light
  useEffect(() => {
    if (currentRoute?.isOpenLedLight) {
      openLedLightApiAction({});
    }
  }, [openLedLightApiAction, currentRoute?.isOpenLedLight]);

  // Tắt LED Light
  useEffect(() => {
    if (currentRoute?.isCloseLedLight) {
      closeLedLightApiAction({});
    }
  }, [closeLedLightApiAction, currentRoute?.isCloseLedLight]);

  // check use webcam
  useEffect(() => {
    if (qs.parseUrl(window?.location?.href)?.query?.[QUERY_STRING.CAM_DEVICE]) {
      setSessionStorage(WEBCAM_STORAGE_KEY, 1);
    }
  }, []);

  // initial loading
  useTimeout(
    () => dispatch(setBoothStateAction({ loadingInitial: false })),
    TIME_INITIAL_APP_DURATION,
  );

  return (
    <Context.Provider
      value={useMemo(
        () => ({
          listLayoutPhotos: listLayoutPhotos ?? null,
          listFramePhotos: listFramePhotos ?? null,
          listThemePhotos: listThemePhotos ?? null,
          listStickerPhotos: listStickerPhotos ?? null,
          listBackgroundPhotos: listBackgroundPhotos ?? null,
          listCapturePosition: listCapturePosition ?? null,
          themeDetail: themeDetail ?? null,
          listThemeCategoryPhotos: listThemeCategoryPhotos ?? null,
          listBackgroundCategoryPhotos: listBackgroundCategoryPhotos ?? null,
          listStickerCategoryPhotos: listStickerCategoryPhotos ?? null,
          listFilterPhotos: listFilterPhotos ?? null,
          listCameras: listCameras ?? null,
          listPhotoTakens:
            isHasErrorListTaken || !listPhotoTakens ? null : listPhotoTakens,
          playList: playlist ?? null,
          listLanguage: listLanguages ?? null,
          currentLayout,
        }),
        [
          listLayoutPhotos,
          listFramePhotos,
          listThemePhotos,
          listStickerPhotos,
          listBackgroundPhotos,
          listCapturePosition,
          themeDetail,
          listThemeCategoryPhotos,
          listBackgroundCategoryPhotos,
          listStickerCategoryPhotos,
          listFilterPhotos,
          listCameras,
          isHasErrorListTaken,
          listPhotoTakens,
          playlist,
          listLanguages,
          currentLayout,
        ],
      )}
    >
      {children}
    </Context.Provider>
  );
};

export const useBoothAppContext = () => useContext(Context);
